import React, { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Combobox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  Field,
  FieldControl,
  FieldError,
  FieldLabel,
  IconButton,
  Input,
  RadioGroup,
} from '@foodamigos/ui';
import { X } from 'lucide-react';
import RadioCard from '../RadioCard';
import useWindowSize from '../../hooks/utility/useWindowSize';
import {
  NewOwnerChoice,
  OwnerChangeError,
  PreviousOwnerOutcome,
  buildChangeOwnerPayload,
  checkNewOwnerEmail,
  isChoiceComplete,
  readChangeOwnerError,
} from '../../services/team/changeOwner';
import { ManagerRole } from '../../services/permissions/PermissionResolver';

/** From this width the dialog is a centred Dialog, below it a bottom sheet (design system rule). */
const DIALOG_MIN_WIDTH = 980;

const EMPTY_INVITE = { first_name: '', last_name: '', email: '' };

// The whole body scrolls in its own padding, so no focus ring is clipped; title and buttons stay pinned,
// each behind a line (design `change-owner`). The negative margins undo the content's own padding;
// shrink-0 because the content is a flex column, which would squash them once the body overflows.
const DIALOG_PARTS = {
  headerClass:
    'sticky shrink-0 -top-6 z-[1] -mx-6 -mt-6 border-b-[1px] border-[var(--fa-color-border)] bg-[var(--fa-color-surface)] pb-[var(--fa-space-4)] pl-6 pr-16 pt-6',
  footerClass:
    'sticky shrink-0 -bottom-6 z-[1] -mx-6 -mb-6 border-t-[1px] border-[var(--fa-color-border)] bg-[var(--fa-color-surface)] px-6 pb-6 pt-[var(--fa-space-4)]',
  closeClass: 'absolute right-5 top-5',
  Root: Dialog,
  Content: DialogContent,
  Header: DialogHeader,
  Title: DialogTitle,
  Description: DialogDescription,
  Footer: DialogFooter,
};

const DRAWER_PARTS = {
  headerClass:
    'sticky shrink-0 -top-2 z-[1] -mx-4 -mt-2 border-b-[1px] border-[var(--fa-color-border)] bg-[var(--fa-color-surface)] pb-[var(--fa-space-3)] pl-4 pr-14 pt-2',
  footerClass:
    'sticky shrink-0 -bottom-6 z-[1] -mx-4 -mb-6 border-t-[1px] border-[var(--fa-color-border)] bg-[var(--fa-color-surface)] px-4 pb-6 pt-[var(--fa-space-3)]',
  closeClass: 'absolute right-4 top-2',
  Root: Drawer,
  Content: DrawerContent,
  Header: DrawerHeader,
  Title: DrawerTitle,
  Description: DrawerDescription,
  Footer: DrawerFooter,
};

/**
 * Change or remove a restaurant's owner (PROD-637, design `change-owner`).
 *
 * Step 1 asks who takes over (someone on the team, someone new, or nobody) and, when
 * there is an owner, what happens to them; step 2 confirms. With no owner ("Choose an
 * owner") only the first question is asked and "No owner for now" is left out.
 *
 * `onSubmit(payload)` sends PUT /business-managers/owner and resolves on success or
 * rejects with the response body; the caller shows the toast and refreshes the list.
 */
export default function ChangeOwnerDialog({
  open,
  onOpenChange,
  restaurant,
  owner = null,
  candidates = [],
  loadingCandidates = false,
  groupName = null,
  returnFocusTo = null,
  onSubmit,
  onFailed,
}) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:Team:Managers:ChangeOwnerDialog' });
  const { width } = useWindowSize();
  // useWindowSize has no width on the first render; reading the window avoids a Dialog flashing before the Drawer.
  const Parts = (width ?? window.innerWidth) >= DIALOG_MIN_WIDTH ? DIALOG_PARTS : DRAWER_PARTS;

  const [step, setStep] = useState('choose');
  const [choice, setChoice] = useState(null);
  const [managerId, setManagerId] = useState(null);
  const [invite, setInvite] = useState(EMPTY_INVITE);
  const [previous, setPrevious] = useState(null);
  const [emailError, setEmailError] = useState(null);
  const [comboOpen, setComboOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const emailRef = useRef(null);
  const comboRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    setStep('choose');
    setChoice(null);
    setManagerId(null);
    setInvite(EMPTY_INVITE);
    setPrevious(null);
    setEmailError(null);
    setComboOpen(false);
    setSaving(false);
    setFailed(false);
  }, [open]);

  useEffect(() => {
    if (emailError) {
      emailRef.current?.focus();
    }
  }, [emailError]);

  // Choosing "Someone on the team" puts the cursor in the search field (spec).
  useEffect(() => {
    if (choice === NewOwnerChoice.Team && comboOpen) {
      comboRef.current?.focus();
    }
  }, [choice, comboOpen]);

  const hasOwner = owner !== null;
  const roleLabel = (manager) =>
    (manager.roles ?? []).includes(ManagerRole.StoreOperator) ? t('roles.operator') : t('roles.employee');
  const picked = candidates.find((manager) => manager.id === managerId) ?? null;
  const options = candidates.map((manager) => ({
    value: manager.id,
    label: manager.name,
    description: `${roleLabel(manager)} · ${manager.email}`,
    keywords: [manager.email],
  }));
  const nextName = {
    [NewOwnerChoice.Team]: picked?.name,
    [NewOwnerChoice.New]: `${invite.first_name} ${invite.last_name}`.trim(),
  }[choice];

  const close = (next) => {
    if (saving) {
      return;
    }

    onOpenChange(next);
  };

  const choose = (value) => {
    setChoice(value);
    setEmailError(null);
    setComboOpen(value === NewOwnerChoice.Team);
  };

  const pickTeamMember = (id) => {
    setChoice(NewOwnerChoice.Team);
    setManagerId(id);
    setEmailError(null);
  };

  const inviteSomeoneNew = (query) => {
    setComboOpen(false);
    setChoice(NewOwnerChoice.New);

    if (query.includes('@')) {
      setInvite((current) => ({ ...current, email: query.trim() }));
    }
  };

  const goToConfirm = () => {
    if (choice === NewOwnerChoice.New) {
      const problem = checkNewOwnerEmail(invite.email, { owner, candidates });

      if (problem) {
        setEmailError(problem);

        return;
      }
    }

    setFailed(false);
    setStep('confirm');
  };

  const confirm = async () => {
    setSaving(true);
    setFailed(false);

    try {
      await onSubmit(
        buildChangeOwnerPayload({ companyId: restaurant.id, owner, choice, managerId, invite, previous }),
        { choice, previous, name: nextName, email: invite.email.trim() },
      );
      setSaving(false);
      onOpenChange(false);
    } catch (body) {
      const error = readChangeOwnerError(body);
      setSaving(false);
      onFailed?.(error.code ?? 'unknown');

      if (error.isEmailError && choice === NewOwnerChoice.New) {
        setEmailError({ code: error.code, manager: error.manager });
        setStep('choose');

        return;
      }

      setFailed(true);
    }
  };

  const emailMessage = () => {
    if (!emailError) {
      return null;
    }

    if (emailError.code === OwnerChangeError.EmailIsManager && emailError.manager) {
      const holder = candidates.find((manager) => manager.id === emailError.manager.id);

      return (
        <>
          {t('email_errors.email_is_manager', { name: emailError.manager.name })}{' '}
          {holder && (
            <Button
              variant="link"
              size="sm"
              onClick={() => pickTeamMember(holder.id)}
              data-testid="change-owner-pick-instead"
            >
              {t('buttons.choose_instead', { name: holder.name })}
            </Button>
          )}
        </>
      );
    }

    return t(`email_errors.${emailError.code}`, { name: emailError.manager?.name ?? '' });
  };

  const inviteField = (key, type = 'text') => {
    const isEmail = key === 'email';
    const invalid = isEmail && Boolean(emailError);
    const id = `change-owner-${key}`;

    return (
      <Field invalid={invalid} id={id}>
        <FieldLabel>{t(`fields.${key}`)}</FieldLabel>
        <Input
          id={id}
          ref={isEmail ? emailRef : undefined}
          type={type}
          autoComplete="off"
          invalid={invalid}
          aria-describedby={invalid ? `${id}-error` : undefined}
          value={invite[key]}
          onChange={(event) => {
            const { value } = event.target;
            setInvite((current) => ({ ...current, [key]: value }));

            if (isEmail) {
              setEmailError(null);
            }
          }}
        />
        {invalid && (
          <FieldError id={`${id}-error`} className="leading-[var(--fa-line-normal)]">
            {emailMessage()}
          </FieldError>
        )}
      </Field>
    );
  };

  const closeButton = !saving && (
    <IconButton
      variant="subtle"
      size="md"
      aria-label={t('buttons.close')}
      className={Parts.closeClass}
      onClick={() => close(false)}
    >
      <X />
    </IconButton>
  );

  const chooseStep = (
    <>
      <Parts.Header className={Parts.headerClass}>
        {closeButton}
        <Parts.Title>
          {hasOwner
            ? t('choose.title', { name: owner.name })
            : t('choose.title_no_owner', { restaurant: restaurant.name })}
        </Parts.Title>
        <Parts.Description className="leading-[var(--fa-line-normal)]">
          {hasOwner
            ? t('choose.description', { name: owner.name, restaurant: restaurant.name })
            : t('choose.description_no_owner', { restaurant: restaurant.name })}
        </Parts.Description>
      </Parts.Header>

      <div className="flex flex-col gap-[var(--fa-space-4)]">
        <Field>
          <FieldLabel id="change-owner-next">{t('choose.new_owner')}</FieldLabel>
          <FieldControl>
            <RadioGroup
              aria-labelledby="change-owner-next"
              value={choice ?? ''}
              onValueChange={choose}
              className="gap-2"
            >
              <RadioCard
                value={NewOwnerChoice.Team}
                testId="change-owner-team"
                selected={choice === NewOwnerChoice.Team}
                disabled={loadingCandidates || candidates.length === 0}
                title={t('options.team.title')}
                description={
                  (loadingCandidates && t('options.team.loading')) ||
                  (candidates.length
                    ? t('options.team.description', { count: candidates.length })
                    : t('options.team.empty'))
                }
              >
                <div className="flex flex-col gap-[var(--fa-space-1)]">
                  <Combobox
                    ref={comboRef}
                    aria-label={t('options.team.title')}
                    options={options}
                    value={managerId ?? ''}
                    onValueChange={pickTeamMember}
                    open={comboOpen}
                    onOpenChange={setComboOpen}
                    placeholder={t('options.team.search')}
                    emptyText={(query) => (
                      <>
                        {t('options.team.no_match', { query })}{' '}
                        <Button variant="link" size="sm" onClick={() => inviteSomeoneNew(query)}>
                          {t('buttons.invite_instead')}
                        </Button>
                      </>
                    )}
                  />
                  {picked && !comboOpen && (
                    <p className="m-0 ml-1 text-[length:var(--fa-font-size-sm)] leading-[var(--fa-line-normal)] text-[var(--fa-color-text-muted)]">
                      {`${roleLabel(picked)} · ${picked.email}`}
                    </p>
                  )}
                </div>
              </RadioCard>
              <RadioCard
                value={NewOwnerChoice.New}
                testId="change-owner-new"
                selected={choice === NewOwnerChoice.New}
                title={t('options.new.title')}
                description={t('options.new.description')}
              >
                <div className="flex flex-col gap-[var(--fa-space-3)]">
                  <div className="grid grid-cols-1 gap-[var(--fa-space-3)] min-[520px]:grid-cols-2">
                    {inviteField('first_name')}
                    {inviteField('last_name')}
                  </div>
                  {inviteField('email', 'email')}
                </div>
              </RadioCard>
              {hasOwner && (
                <RadioCard
                  value={NewOwnerChoice.None}
                  testId="change-owner-none"
                  selected={choice === NewOwnerChoice.None}
                  title={t('options.none.title')}
                  description={t('options.none.description', { restaurant: restaurant.name })}
                />
              )}
            </RadioGroup>
          </FieldControl>
        </Field>

        {hasOwner && (
          <Field>
            <FieldLabel id="change-owner-previous">{t('choose.previous_owner', { name: owner.name })}</FieldLabel>
            <FieldControl>
              <RadioGroup
                aria-labelledby="change-owner-previous"
                value={previous ?? ''}
                onValueChange={setPrevious}
                className="gap-2"
              >
                <RadioCard
                  value={PreviousOwnerOutcome.KeepAsOperator}
                  testId="change-owner-keep"
                  selected={previous === PreviousOwnerOutcome.KeepAsOperator}
                  title={t('options.keep.title')}
                  description={t('options.keep.description')}
                />
                <RadioCard
                  value={PreviousOwnerOutcome.Remove}
                  testId="change-owner-remove"
                  selected={previous === PreviousOwnerOutcome.Remove}
                  title={t('options.remove.title')}
                  description={t('options.remove.description')}
                />
              </RadioGroup>
            </FieldControl>
          </Field>
        )}
      </div>

      <Parts.Footer className={Parts.footerClass}>
        <Button
          onClick={goToConfirm}
          disabled={!isChoiceComplete({ choice, managerId, invite, previous, hasOwner })}
          data-testid="change-owner-continue"
        >
          {t('buttons.continue')}
        </Button>
        <Button variant="secondary" onClick={() => close(false)}>
          {t('buttons.cancel')}
        </Button>
      </Parts.Footer>
    </>
  );

  const summary = [];

  if (choice === NewOwnerChoice.None) {
    summary.push(['none', 'summary.none', { restaurant: restaurant.name }]);
  } else if (choice === NewOwnerChoice.New) {
    summary.push(['new', 'summary.new', { name: nextName, restaurant: restaurant.name }]);
    summary.push(['email', 'summary.email', { email: invite.email.trim() }]);
  } else {
    summary.push(['team', 'summary.team', { name: nextName, restaurant: restaurant.name }]);
    // A team member becomes owner of every restaurant of the group, which can be more than they had.
    if (groupName) {
      summary.push(['access', 'summary.group_access', { group: groupName }]);
    }
  }

  if (hasOwner) {
    summary.push(
      previous === PreviousOwnerOutcome.KeepAsOperator
        ? ['previous', 'summary.keep', { name: owner.name }]
        : ['previous', 'summary.remove', { name: owner.name }],
    );
  }

  const confirmStep = (
    <>
      <Parts.Header className={Parts.headerClass}>
        {closeButton}
        <Parts.Title>
          {hasOwner ? t('confirm.title', { name: owner.name }) : t('confirm.title_no_owner', { name: nextName })}
        </Parts.Title>
        <Parts.Description className="leading-[var(--fa-line-normal)]">
          {hasOwner && choice !== NewOwnerChoice.None ? t('confirm.description_both') : t('confirm.description')}
        </Parts.Description>
      </Parts.Header>

      <ul
        data-testid="change-owner-summary"
        className="m-0 flex list-disc flex-col gap-[var(--fa-space-2)] rounded-[var(--fa-radius-md)] bg-[var(--fa-color-surface-sunken)] py-[var(--fa-space-4)] pl-[var(--fa-space-8)] pr-[var(--fa-space-4)] text-[length:var(--fa-font-size-md)] text-[var(--fa-color-text)]"
      >
        {summary.map(([key, i18nKey, values]) => (
          <li key={key}>
            <Trans t={t} i18nKey={i18nKey} values={values} components={{ b: <b /> }} />
          </li>
        ))}
      </ul>

      {failed && (
        <Alert variant="danger" data-testid="change-owner-error">
          <AlertTitle>{t('error.title')}</AlertTitle>
          <AlertDescription>{t('error.description')}</AlertDescription>
        </Alert>
      )}

      <Parts.Footer className={Parts.footerClass}>
        <Button
          variant={hasOwner ? 'danger' : 'primary'}
          loading={saving}
          onClick={confirm}
          data-testid="change-owner-confirm"
        >
          {saving && t('buttons.saving')}
          {!saving && failed && t('buttons.try_again')}
          {!saving && !failed && (hasOwner ? t('buttons.remove_as_owner') : t('buttons.make_owner'))}
        </Button>
        <Button variant="secondary" onClick={() => setStep('choose')} disabled={saving}>
          {t('buttons.back')}
        </Button>
      </Parts.Footer>
    </>
  );

  return (
    <Parts.Root open={open} onOpenChange={close} {...(Parts === DRAWER_PARTS ? { dismissible: !saving } : {})}>
      <Parts.Content
        ref={contentRef}
        // Start on the first choice, not on the close button that now leads the dialog.
        onOpenAutoFocus={(event) => {
          const first = contentRef.current?.querySelector('[role="radio"]:not([disabled])');

          if (first) {
            event.preventDefault();
            first.focus();
          }
        }}
        // Our own close sits in the pinned title row; the package's would scroll away under it.
        showClose={false}
        onCloseAutoFocus={(event) => {
          // Opened from state, not a Trigger, so focus goes back to the button that opened it.
          if (returnFocusTo?.isConnected) {
            event.preventDefault();
            returnFocusTo.focus();
          }
        }}
        onEscapeKeyDown={(event) => saving && event.preventDefault()}
        onPointerDownOutside={(event) => saving && event.preventDefault()}
        data-testid="change-owner-dialog"
      >
        {step === 'confirm' ? confirmStep : chooseStep}
      </Parts.Content>
    </Parts.Root>
  );
}
